import { isArray, random } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { IssuerAddUpdateAssetModal } from '@/modules/issuer-dashboard/components/portfolios/IssuerAddUpdateAssetModal.tsx';
import {
  useIssuerPortfolioAssetsInfiniteListQuery,
  useIssuerPortfolioAssetsListQuery,
} from '@/modules/issuer-dashboard/queries/portfolios.tsx';
import { SelectPaginate, SelectPaginateOption } from 'ui';
import { AssetExtended } from 'api';

type baseProps = {
  clearable?: boolean;
  label?: string;
  formFieldTitle?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
  withBorder?: boolean;
  registerId?: number;
  placeholder?: string;
  portfolioId: number;
  showBorderBottom?: boolean;
  showAddNewItem?: boolean;
  withBrokers?: boolean;
};

type singleProps = {
  isMulti?: false;
  onChange: (options: SelectPaginateOption<AssetExtended> | null, fieldName: string) => void;
  value?: number;
};

type multiProps = {
  isMulti?: true;
  onChange: (options: SelectPaginateOption<AssetExtended>[] | null, fieldName: string) => void;
  value?: number[];
};

type SelectAssetProps = baseProps & (singleProps | multiProps);

export const SelectAsset = ({
  error,
  label,
  isMulti = false,
  disabled,
  clearable,
  className,
  withBorder = false,
  value,
  formFieldTitle = '',
  onChange,
  registerId,
  placeholder,
  portfolioId,
  showBorderBottom = true,
  showAddNewItem = false,
  withBrokers = true,
}: SelectAssetProps) => {
  const [params] = useSearchParams();
  const [isOpenAdd, setIsOpenAdd] = useState(false);
  const [rand, setRand] = useState(1);
  const register_id = +(params.get(REGISTER_ID_SEARCH_PARAM_KEY) || 0);
  const { fetchNextPage } = useIssuerPortfolioAssetsInfiniteListQuery(
    {
      page: 1,
      page_size: 10,
      register_id: registerId || register_id,
      portfolio_id: portfolioId,
      is_asset_only: !withBrokers,
    },
    true,
  );
  const { data: dataAssets } = useIssuerPortfolioAssetsListQuery(
    {
      page: 1,
      page_size: 50,
      register_id: registerId || register_id,
      portfolio_id: portfolioId,
      is_asset_only: !withBrokers,
    },
    // !value,
  );

  const allAssets = useMemo<SelectPaginateOption<AssetExtended>[]>(() => {
    return (
      (dataAssets?.data || [])
        // ?.filter(item => (withBrokers ? true : !item.is_broker))
        ?.map(item => {
          const { asset_type, currency, name } = item;

          return {
            label: (
              <span className="flex gap-4 items-center justify-between font-[Inter] text-sm pr-3">
                <span className="flex gap-1 items-center font-[Inter] text-sm text-nowrap">
                  <span className="text-gray-900">{name}</span>
                  <span className="text-gray-500">
                    ({`${asset_type || ''} ${currency?.toUpperCase()}`.trim() || '-'})
                  </span>
                </span>
                <span></span>
              </span>
            ),
            value: item,
            toString: () => name,
          };
        })
    );
  }, [dataAssets?.data]);

  const valueWithLabel = useMemo<
    SelectPaginateOption<AssetExtended> | SelectPaginateOption<AssetExtended>[] | null | undefined
  >(() => {
    if (!value) return null;

    const options = isArray(value)
      ? allAssets.filter(option => value?.includes(option.value.id))
      : allAssets.find(option => option.value.id === value);

    return options || null;
    // isArray(options)
    //   ? options.map(item => ({ ...item, value: item.value }))
    //   : options
    //   ? {
    //       label: options?.label,
    //       value: options?.value,
    //     }
    //   : null;
  }, [allAssets, value]);

  const handleAsyncLoadFormOptions = useCallback(
    async (page: number, search?: string) => {
      const dataAssets = await fetchNextPage({ pageParam: { page, search } });

      const assets = dataAssets?.data?.pages?.at(-1)?.data || [];
      return (
        assets
          // ?.filter(item => (withBrokers ? true : !item.is_broker))
          .map(item => {
            const { asset_type, currency, name } = item;
            return {
              label: (
                <span className="flex gap-4 items-center justify-between font-[Inter] text-sm pr-3">
                  <span className="flex gap-1 items-center font-[Inter] text-sm text-nowrap">
                    <span className="text-gray-900">{name}</span>
                    <span className="text-gray-500">
                      ({`${asset_type || ''} ${currency?.toUpperCase()}`.trim() || '-'})
                    </span>
                  </span>
                  <span></span>
                </span>
              ),
              value: item,
              toString: () => name,
            };
          })
      );
    },
    [fetchNextPage],
  );

  const handleOnSuccessAdded = () => {
    setRand(random(true) + 1000);
  };

  return (
    <>
      <SelectPaginate
        cacheUniqs={['assets', `${rand}`, portfolioId, registerId || register_id, withBrokers]}
        isClearable={clearable}
        disabled={disabled}
        isMulti={isMulti}
        label={label}
        className={className}
        onAsync={handleAsyncLoadFormOptions}
        onChange={options => onChange(options as any, formFieldTitle)}
        value={valueWithLabel}
        error={error}
        withBorder={withBorder}
        placeholder={placeholder}
        showBorderBottom={showBorderBottom}
        addedLastOption={
          showAddNewItem
            ? {
                label: 'Add new asset',
                onClick: () => setIsOpenAdd(true),
              }
            : undefined
        }
      />
      <IssuerAddUpdateAssetModal
        onClose={() => setIsOpenAdd(false)}
        open={isOpenAdd}
        onSuccess={handleOnSuccessAdded}
        portfolioId={portfolioId}
      />
    </>
  );
};
